/**
 * The menu (Escape or P, or the pause button where the gear sits in a room; 2026-10-28). Alone in the room it
 * pauses the game: main.ts tells the server the menu is open (`pause`), and the server stops stepping the match
 * until it closes, so waves, timers and countdowns all hold. With anyone else in the room nothing stops (co-op,
 * capture the flag, story with friends); the menu just opens. Resume, the controls, the spellbook and leaving are
 * one click away.
 */

import type { ClientState } from '../state/clientState.js';
import { type Screen, button, clear, el, focusPanel, keyboardFocus, restoreFocus, setText, trapTab } from './dom.js';

export interface PauseMenuDeps {
  openSettings(): void;
  openBook(): void;
  leave(): void;
  /** The key codes that open (and so close) the menu: the keymap's pause bindings. */
  isMenuKey(code: string): boolean;
  onOpenChange(open: boolean): void;
}

export interface PauseMenu extends Screen {
  open(): void;
  close(): void;
  toggle(): void;
  readonly isOpen: boolean;
}

/** The menu's heading and line: "Paused" alone (once the server has frozen the match), "Menu" with others playing. */
export function pauseMenuText(players: number, paused: boolean): { title: string; line: string } {
  if (players > 1) return { title: 'Menu', line: 'Others are playing, so the game keeps going.' };
  return { title: 'Paused', line: paused ? 'Everything is frozen until you resume.' : 'Pausing...' };
}

export function mountPauseMenu(root: HTMLElement, state: ClientState, deps: PauseMenuDeps): PauseMenu {
  clear(root);
  root.classList.add('pause-menu');
  root.hidden = true;

  const title = el('h2', { class: 'pause-title' }, 'Paused');
  const line = el('p', { class: 'muted pause-line' });
  const resume = button('Resume', () => close(), 'btn btn-primary');
  const panel = el('div', { class: 'pause-panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Menu', tabindex: -1 },
    title,
    line,
    el('div', { class: 'pause-actions' },
      resume,
      // Settings and the pinned spellbook count as the menu too (main.ts), so a solo game stays paused behind them.
      button('Controls and sound', () => { close(); deps.openSettings(); }, 'btn'),
      button('Spellbook', () => { close(); deps.openBook(); }, 'btn'),
      button('Leave the room', () => { close(); deps.leave(); }, 'btn btn-ghost'),
    ),
  );
  root.appendChild(panel);

  let opener: HTMLElement | null = null;

  function refresh(): void {
    // Everyone in the room, a friend still reconnecting too: the server pauses only for a room of one.
    const t = pauseMenuText(state.room?.players.length ?? 1, state.paused);
    setText(title, t.title);
    setText(line, t.line);
  }

  function open(): void {
    if (!root.hidden || !state.inRoom) return;
    opener = keyboardFocus();
    refresh();
    root.hidden = false;
    deps.onOpenChange(true);
    focusPanel(panel);
  }

  function close(): void {
    if (root.hidden) return;
    root.hidden = true;
    deps.onOpenChange(false);
    restoreFocus(opener, root);
    opener = null;
  }

  // Capture phase: while the menu is open the game's keys are off, so the menu key and Escape close it here.
  const onKey = (ev: KeyboardEvent): void => {
    // A press the game already took (the one that just opened the menu, when it reached the game first) is not a close.
    if (root.hidden || ev.defaultPrevented) return;
    if (ev.code === 'Escape' || deps.isMenuKey(ev.code)) {
      ev.preventDefault();
      ev.stopPropagation();
      close();
      return;
    }
    trapTab(ev, panel);
  };
  window.addEventListener('keydown', onKey, true);

  const unsubscribe = state.subscribe(() => {
    if (root.hidden) return;
    // Out of the room (left, kicked, crashed): nothing to pause.
    if (!state.inRoom) { close(); return; }
    refresh();
  });

  return {
    show: open,
    hide: close,
    open,
    close,
    toggle(): void { if (root.hidden) open(); else close(); },
    get isOpen(): boolean { return !root.hidden; },
    dispose(): void {
      unsubscribe();
      window.removeEventListener('keydown', onKey, true);
      clear(root);
    },
  };
}
